import React, { useState } from "react";
import { Trash2, Search, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { adminApi } from "../../../utils/api";

// Default cutoff: 90 days ago
function defaultCutoff() {
  const d = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  return d.toISOString().split("T")[0];
}

export default function CleanupAction({ onMessage, onError }) {
  const [cutoffDate, setCutoffDate] = useState(defaultCutoff);
  const [staleOnly, setStaleOnly] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { events_affected, occurrences_to_remove }
  const [executing, setExecuting] = useState(false);

  const handlePreview = async () => {
    if (!cutoffDate) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const result = await adminApi.previewCleanup(cutoffDate, staleOnly);
      setPreview(result);
    } catch (err) {
      onError(err.response?.data?.detail || "Error running preview");
    } finally {
      setPreviewing(false);
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    try {
      const result = await adminApi.executeCleanup(cutoffDate, staleOnly);
      onMessage(result.message || "Cleanup completed");
      setPreview(null);
    } catch (err) {
      onError(err.response?.data?.detail || "Error executing cleanup");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-4 rounded-lg border border-border">
      <div>
        <h4 className="font-medium flex items-center gap-2">
          <Trash2 className="h-4 w-4" />
          Cleanup: Old Occurrences
        </h4>
        <p className="text-sm text-muted-foreground mt-1">
          Remove past occurrences from recurring events (museums, permanent shows, etc.).
          Trims occurrence arrays in-place without deleting events.
        </p>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="min-w-0">
          <label className="block text-sm font-medium mb-1">Cutoff date</label>
          <input
            type="date"
            value={cutoffDate}
            onChange={(e) => { setCutoffDate(e.target.value); setPreview(null); }}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring sm:w-auto"
          />
        </div>
        <label className="flex flex-wrap items-center gap-2 text-sm pb-0.5 cursor-pointer">
          <input
            type="checkbox"
            checked={staleOnly}
            onChange={(e) => { setStaleOnly(e.target.checked); setPreview(null); }}
            className="rounded border-input"
          />
          Stale events only
          <span className="text-muted-foreground">(no future dates)</span>
        </label>
        <Button variant="outline" onClick={handlePreview} disabled={!cutoffDate || previewing}>
          {previewing ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Calculating...</>
          ) : (
            <><Search className="h-4 w-4 mr-2" />Preview</>
          )}
        </Button>
      </div>

      {preview && (
        <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/20 text-sm">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <span>
                <strong>{preview.events_affected.toLocaleString()}</strong> events affected &middot;{" "}
                <strong>{preview.occurrences_to_remove.toLocaleString()}</strong> occurrences to remove
              </span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleExecute}
                disabled={executing || preview.events_affected === 0}
              >
                {executing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running...</>
                ) : (
                  "Execute Cleanup"
                )}
              </Button>
              <button
                className="text-sm underline text-muted-foreground hover:text-foreground"
                onClick={() => setPreview(null)}
              >
                Cancel
              </button>
            </div>
          </div>
          {preview.events_affected === 0 && (
            <p className="mt-1 text-muted-foreground">No events match the current criteria.</p>
          )}
        </div>
      )}
    </div>
  );
}
